import { useEffect, useRef } from 'react';

import monitoring from '@editor/services/monitoring';
import analytics from '@repo/analytics';
import { User } from '@repo/domain/user';
import { subscriptionApi, type BillingInvoice } from '@repo/subscription/api';
import { createHash } from 'crypto-browserify';

import {
  SUBSCRIPTION_SEE_SUCCESS,
  SUBSCRIPTION_TRIAL_STARTED,
} from '../../../global/analytics/events';
import useFreeTrial from '../../../hooks/useFreeTrial';
import { useUserStore, userStoreSelector } from '../../../stores/userStore';
import { MonetaryValue } from '../../../types/Pricing';
import { SupportedCurrency } from '../../../types/SubscriptionPrice';
import { getAdClickId } from '../../../utils/adClickId';
import { formatMonetaryValue } from '../../../utils/billing';
import { trimLowercase } from '../../../utils/string';

const getMonetaryValue = (
  amount?: number | null,
  currency?: SupportedCurrency
): MonetaryValue => ({
  amount: amount ?? 0,
  currency: currency ?? SupportedCurrency.USD,
});
const sanitize = (value?: string): string | undefined =>
  value ? encodeURIComponent(trimLowercase(value)) : undefined;

// https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/customer-information-parameters/
const fbData = (
  user: User,
  subscriptionId?: string
): Record<string, string | undefined> => {
  const [firstName, lastName] = (user.name?.split(' ') ?? []).map(sanitize);
  const country = sanitize(user.country.current ?? '');
  const email = sanitize(user.email);

  return {
    em: createHash('sha256').update(email).digest('hex'),
    fn: firstName
      ? createHash('sha256').update(firstName).digest('hex')
      : undefined,
    ln: lastName
      ? createHash('sha256').update(lastName).digest('hex')
      : undefined,
    country: country
      ? createHash('sha256').update(country).digest('hex')
      : undefined,
    client_user_agent: navigator.userAgent,
    external_id: createHash('sha256').update(user.id).digest('hex'),
    fbc: getAdClickId(),
    subscription_id: subscriptionId,
    client_ip_address: user.ip,
  };
};

const eCommerceData = (
  invoiceData: BillingInvoice
):
  | {
      currencyCode: string;
      purchase: {
        actionField: {
          id: string;
          revenue: string;
          tax: string;
          price: string;
          coupon: string | undefined;
        };
        products: {
          id?: string;
          name: string;
          price: string;
          quantity: number;
        }[];
      };
    }
  | undefined => {
  if (!invoiceData.subscriptionId) {
    console.error('Missing subscriptionId in invoiceData', invoiceData);
    monitoring.captureException('Missing subscriptionId in invoiceData', {
      invoiceData,
    });

    return;
  }

  const tax = formatMonetaryValue(getMonetaryValue(invoiceData.amountTax));
  const revenue = formatMonetaryValue(
    getMonetaryValue(invoiceData.amountTotal, invoiceData.currency)
  );
  const price = formatMonetaryValue(
    getMonetaryValue(invoiceData.priceAmount, invoiceData.currency)
  );

  return {
    currencyCode: invoiceData.currency?.toUpperCase() ?? 'USD',
    purchase: {
      actionField: {
        id: invoiceData.subscriptionId,
        revenue,
        tax,
        price,
        coupon: invoiceData.couponId,
      },
      products: invoiceData.items.map((item) => ({
        id: item.priceId,
        name: item.name,
        price: formatMonetaryValue(
          getMonetaryValue(item.price, invoiceData.currency)
        ),
        // TODO: remove ts-ignore once BE is merged https://app.clickup.com/t/86c2ztc52
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        quantity: item.quantity ?? 1,
      })),
    },
  };
};

/** @knipignore - used in website */
export const trackSubscriptionSeeSuccess = (
  user: User,
  invoiceData: BillingInvoice
): void => {
  const analyticsData = {
    userId: user.id,
    leadsUserData: {
      email: user.email,
    },
    fb: fbData(user, invoiceData.subscriptionId),
    plan: user.plan,
    isTeam: user.isTeam,
    ecommerce: eCommerceData(invoiceData),
  };
  void analytics.track(SUBSCRIPTION_SEE_SUCCESS, analyticsData);
};

const trackFreeTrialSuccess = (
  user: User,
  invoiceData: BillingInvoice
): void => {
  const analyticsData = {
    userId: user.id,
    leadsUserData: {
      email: user.email,
    },
    fb: fbData(user, invoiceData.subscriptionId),
    plan: user.plan,
    isTeam: user.isTeam,
    ecommerce: eCommerceData(invoiceData),
  };
  void analytics.track(SUBSCRIPTION_TRIAL_STARTED, analyticsData);
};

const useSubscriptionSuccessTracking = (singlePayment?: boolean): void => {
  const user = useUserStore(userStoreSelector.user);
  const lockTrackSuccess = useRef(false);
  const { isRunning: isFreeTrialRunning } = useFreeTrial();

  useEffect(() => {
    if (singlePayment) {
      // Single payment is not subscription, so no need to track
      return;
    }

    const trackSuccess = async (): Promise<void> => {
      if (user) {
        const invoiceData = await subscriptionApi.getLatestInvoice();
        if ('error' in invoiceData) {
          return;
        }

        if (!isFreeTrialRunning) {
          trackSubscriptionSeeSuccess(user, invoiceData);
        } else {
          trackFreeTrialSuccess(user, invoiceData);
        }
        lockTrackSuccess.current = true;
      }
    };

    if (!lockTrackSuccess.current) {
      void trackSuccess();
    }
  }, [user, isFreeTrialRunning, singlePayment]);
};

export default useSubscriptionSuccessTracking;
